import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { AuthorDoc } from '../../services/AuthorFirestoreService';

export interface AuthorPickerScreenProps {
  authors: AuthorDoc[];
  loading?: boolean;
  invalidAuthorId?: string | null;
  onSelect: (authorId: string) => void;
  onCreate: () => void;
  onOverview?: () => void;
}

export const AuthorPickerScreen: React.FC<AuthorPickerScreenProps> = ({
  authors,
  loading = false,
  invalidAuthorId,
  onSelect,
  onCreate,
  onOverview,
}) => {
  return (
    <Box
      data-testid="content-plan-author-picker"
      sx={{
        minHeight: 'calc(100vh - 96px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
      }}
    >
      <Stack spacing={2.5} sx={{ width: '100%', maxWidth: 920 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Choose author workspace</Typography>
            <Typography variant="body2" color="text.secondary">
              Content plans, topics, posts, and calendar items are scoped per author.
            </Typography>
          </Box>
          {authors.length === 0 && (
            <Button startIcon={<AddIcon />} variant="contained" onClick={onCreate} data-testid="author-picker-create">
              Add author
            </Button>
          )}
        </Stack>

        {invalidAuthorId && (
          <Alert severity="warning">Author "{invalidAuthorId}" was not found in this project.</Alert>
        )}

        {loading ? (
          <Alert severity="info">Loading authors...</Alert>
        ) : authors.length === 0 ? (
          <Alert severity="info">
            No authors exist yet. Add the first baseline author to start.
          </Alert>
        ) : (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
          >
            {authors.map((author) => (
              <Paper
                key={author.id}
                variant="outlined"
                role="button"
                tabIndex={0}
                onClick={() => author.id && onSelect(author.id)}
                onKeyDown={(event) => {
                  if ((event.key === 'Enter' || event.key === ' ') && author.id) onSelect(author.id);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  borderRadius: 1,
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
                data-testid={`author-picker-card-${author.id}`}
              >
                <Stack spacing={1}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {author.displayName ?? author.id}
                    </Typography>
                    {author.isDefault && <Chip size="small" color="primary" label="Default" />}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
                    {author.positioning ?? author.uniqueValueProposition ?? 'No positioning saved yet.'}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={author.isComplete ? 'success' : 'warning'}
                    label={author.isComplete ? 'Ready' : 'Setup incomplete'}
                    sx={{ alignSelf: 'flex-start' }}
                  />
                </Stack>
              </Paper>
            ))}
          </Box>
        )}

        {authors.length > 1 && onOverview && (
          <Button variant="text" onClick={onOverview} sx={{ alignSelf: 'flex-start' }} data-testid="author-picker-overview">
            View all authors overview
          </Button>
        )}
      </Stack>
    </Box>
  );
};
